/**
 * The keys a list screen answers. Mounted by the screen, not by `DataTable`, because
 * "open the focused row" is the screen's decision.
 *
 * Every handler no-ops while focus is inside an input, textarea or contenteditable, so
 * typing `j` in the search box types a `j`.
 */
import { useEffect, useRef, useState } from "react";

/// The id `FilterBar` puts on its search input. Owned here rather than there so a hook
/// in `lib/` never has to import a component from `components/`.
export const SEARCH_INPUT_ID = "filterbar-search";

function inAField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || target.isContentEditable;
}

export function useListKeys<T>({
  rows,
  rowKey,
  onOpen,
  selection,
  onClearFilters,
}: {
  rows: T[];
  rowKey: (row: T) => string;
  onOpen: (row: T) => void;
  selection?: { selected: ReadonlySet<string>; onChange: (next: ReadonlySet<string>) => void };
  onClearFilters: () => void;
}): { focused: number } {
  const [focused, setFocused] = useState(0);
  const anchor = useRef<number | null>(null);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (inAField(event.target)) return;

      switch (event.key) {
        case "/": {
          event.preventDefault();
          document.getElementById(SEARCH_INPUT_ID)?.focus();
          return;
        }
        case "j": {
          event.preventDefault();
          setFocused((at) => Math.min(at + 1, Math.max(rows.length - 1, 0)));
          return;
        }
        case "k": {
          event.preventDefault();
          setFocused((at) => Math.max(at - 1, 0));
          return;
        }
        case "Enter": {
          const row = rows[focused];
          if (row) {
            event.preventDefault();
            onOpen(row);
          }
          return;
        }
        case "X":
        case "x": {
          if (!selection) return;
          const row = rows[focused];
          if (!row) return;
          event.preventDefault();
          const next = new Set(selection.selected);
          if (event.shiftKey && anchor.current !== null) {
            const [start, end] =
              anchor.current <= focused ? [anchor.current, focused] : [focused, anchor.current];
            for (let at = start; at <= end; at += 1) {
              const one = rows[at];
              if (one) next.add(rowKey(one));
            }
          } else {
            const key = rowKey(row);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            anchor.current = focused;
          }
          selection.onChange(next);
          return;
        }
        case "Escape": {
          // Selection first, filters second: the narrower undo is the one a person means.
          if (selection && selection.selected.size > 0) {
            event.preventDefault();
            selection.onChange(new Set());
            return;
          }
          event.preventDefault();
          onClearFilters();
          return;
        }
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [rows, rowKey, onOpen, selection, onClearFilters, focused]);

  return { focused };
}
